import { ChartData, ChartOptions, ChartType, TooltipItem } from 'chart.js/auto';
import ChartjsPluginDatalabels from 'chartjs-plugin-datalabels';
import ChartjsPluginStacked100Module from 'chartjs-plugin-stacked100';
import 'chartjs-adapter-dayjs-4/dist/chartjs-adapter-dayjs-4.esm';
import { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';

// Vite 8 changed CJS interop — the default import may resolve to the module
// namespace object instead of the default export itself.
const ChartjsPluginStacked100 =
  ChartjsPluginStacked100Module.default ?? ChartjsPluginStacked100Module;

import { cn } from 'cn';

import { AggregationInterval } from '~/modules/dashboard';

import { getFormattedNumber, mergeDeep } from '@vestigas/utils';
import { currentLocale } from '~/utils';

import { CHART_HOVER_COLOR } from '../constants';

type P = {
  aggregationInterval: AggregationInterval;
  data: ChartData;
  formatTooltipLabel?: (context: TooltipItem<ChartType>) => string;
  formatTooltipTitle?: (title: string) => string;
  formatXAxisLabel?: (label: string) => string;
  formatYAxisLabel?: (value: number) => string;
  onClick?: (event: any, elements: any[], chart: any) => void;
  options?: ChartOptions<'bar'>;
  showDataLabels?: boolean;
  suggestedMax?: number;
} & ComponentStyling;

const locale = currentLocale();

const formatXAxisLabelDefault = (label) => {
  const formattedDate = Intl.DateTimeFormat(locale, {
    month: 'long',
  }).format(new Date(label));

  return formattedDate;
};

/**
 * TimeSeriesChart
 *
 * Displays a time series chart with the given data and options.
 */
export const TimeSeriesChart = ({
  aggregationInterval = 'week',
  className,
  data,
  formatTooltipLabel,
  formatTooltipTitle,
  formatXAxisLabel = formatXAxisLabelDefault,
  formatYAxisLabel,
  onClick,
  options,
  showDataLabels = false,
  style,
  suggestedMax,
}: P) => {
  const chartData = data ?? { datasets: [], labels: [] };

  // Rotate data labels if there are too many bars to avoid overlapping (basic implementation).
  // Ideally this would take into account the width of values themselves, but that's much more complex to do.
  const shouldRotateDataLabels = (data?.datasets?.[0]?.data?.length ?? 0) > 12;

  const chartOptions = useMemo(
    () =>
      mergeDeep(
        {
          animation: false,
          datasets: {
            bar: {
              borderColor: '#fff',
              borderWidth: ({ datasetIndex }: { datasetIndex: number }) => {
                const indexAxis = options?.indexAxis ?? 'x';

                // Hack to separate stacked bars with a 1px border at the top of each bar.
                if (
                  data?.datasets?.length &&
                  data.datasets.length > 1 &&
                  datasetIndex < data.datasets.length - 1
                ) {
                  return {
                    bottom: 0,
                    left: 0,
                    right: indexAxis === 'y' ? 1 : 0,
                    top: indexAxis === 'x' ? 1 : 0,
                  };
                }

                // No border for the topmost dataset
                return 0;
              },
              hoverBackgroundColor: CHART_HOVER_COLOR,
              maxBarThickness: 28,
            },
          },
          indexAxis: 'x',
          locale,
          maintainAspectRatio: false,
          onClick,
          plugins: {
            datalabels: {
              align: 'end',
              anchor: 'end',
              color: '#979eb4', // Perceptively adjusted to match bar color
              display: Boolean(showDataLabels),
              font: {
                size: 14,
              },
              formatter: ({
                x: _label,
                y: value,
              }: {
                x: string;
                y: number;
              }) => {
                return getFormattedNumber({ value, digits: 0 });
              },
              offset: shouldRotateDataLabels ? 6 : 3,
              padding: 0,
              rotation: shouldRotateDataLabels ? 270 : 0,
            },
            legend: {
              align: 'end',
              display: false, // Has to be actively enabled in the options
              labels: {
                borderRadius: 2,
                boxHeight: 10,
                boxWidth: 10,
                useBorderRadius: true,
              },
              position: 'bottom',
            },
            tooltip: {
              boxPadding: 6,
              callbacks: {
                label: formatTooltipLabel,
                title: formatTooltipTitle,
              },
              displayColors: false,
              padding: {
                x: 10,
                y: 8,
              },
            },
          },
          scales: {
            x: {
              beginAtZero: true,
              grid: {
                display: false,
              },
              stacked: true,
              ticks: {
                callback: formatXAxisLabel,
              },
              time: {
                unit: aggregationInterval,
              },
              type: 'time',
            },
            y: {
              beginAtZero: true,
              border: {
                display: false,
              },
              grid: {
                display: true,
                tickLength: 0,
              },
              suggestedMax,
              ticks: {
                callback: formatYAxisLabel,
                padding: 6,
              },
            },
          },
        },
        options,
      ),
    [
      aggregationInterval,
      data?.datasets?.length,
      formatTooltipLabel,
      formatTooltipTitle,
      formatXAxisLabel,
      formatYAxisLabel,
      locale,
      onClick,
      options,
      shouldRotateDataLabels,
      showDataLabels,
      suggestedMax,
    ],
  );

  return (
    <div
      className={cn('relative h-80 w-full min-w-0 overflow-hidden', className)}
      style={style}
    >
      <Bar
        data={chartData}
        options={chartOptions}
        plugins={[ChartjsPluginStacked100, ChartjsPluginDatalabels]}
      />
    </div>
  );
};
